// src/services/personalItemService.ts
import axios from '../api/axios';

export interface PersonalItem {
  id: number;
  itemName: string;
  localizedName?: string;
  displayName?: string;
  shortName?: string;
  description?: string;
  itemType?: string;
  subType?: string;
  category?: string;
  size: number;
  grade: number;
  manufacturerGuid?: string;
  tags?: string;
  requiredTags?: string;
  inventorySizeMicroScu: number;
  inventorySizeScu: number;
  xmlFilePath?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ShopLocation {
  id: number;
  shopId: string;
  shopName: string;
  locationName?: string;
  planetSystem?: string;
  shopType?: string;
}

export interface ItemStats {
  total: number;
  byCategory: Array<{ category: string; count: number }>;
}

export interface GetItemsParams {
  category?: string;
  itemType?: string;
  subType?: string;
  search?: string;
  skip?: number;
  take?: number;
}

export interface FilterOptions {
  itemTypes: Array<{ type: string; count: number }>;
  subTypes: Array<{ subType: string; count: number }>;
  categories: Array<{ category: string; count: number }>;
  totalItems: number;
}

class PersonalItemService {
  async getPersonalItems(params: GetItemsParams = {}): Promise<PersonalItem[]> {
    const response = await axios.get('/PersonalItems', { params });
    return response.data;
  }

  async getPersonalItem(id: number): Promise<PersonalItem> {
    const response = await axios.get(`/PersonalItems/${id}`);
    return response.data;
  }

  async getPersonalItemByName(name: string): Promise<PersonalItem> {
    const response = await axios.get(`/PersonalItems/by-name/${encodeURIComponent(name)}`);
    return response.data;
  }

  async getCategories(): Promise<string[]> {
    const response = await axios.get('/PersonalItems/categories');
    return response.data;
  }

  async getItemTypes(): Promise<string[]> {
    const response = await axios.get('/PersonalItems/types');
    return response.data;
  }

  async getSubTypes(itemType?: string): Promise<string[]> {
    const params: any = {};
    if (itemType) params.itemType = itemType;
    const response = await axios.get('/PersonalItems/subtypes', { params });
    return response.data;
  }

  async getFilterOptions(): Promise<FilterOptions> {
    const response = await axios.get('/PersonalItems/filter-options');
    return response.data;
  }

  async getStats(): Promise<ItemStats> {
    const response = await axios.get('/PersonalItems/stats');
    return response.data;
  }

  async getShopsForItem(id: number): Promise<ShopLocation[]> {
    const response = await axios.get(`/PersonalItems/${id}/shops`);
    return response.data;
  }

  // Shop endpoints
  async getShops(location?: string, shopType?: string): Promise<ShopLocation[]> {
    const params: any = {};
    if (location) params.location = location;
    if (shopType) params.shopType = shopType;
    const response = await axios.get('/Shops', { params });
    return response.data;
  }

  async getShop(id: number): Promise<ShopLocation> {
    const response = await axios.get(`/Shops/${id}`);
    return response.data;
  }

  async getLocations(): Promise<string[]> {
    const response = await axios.get('/Shops/locations');
    return response.data;
  }

  async getShopTypes(): Promise<string[]> {
    const response = await axios.get('/Shops/types');
    return response.data;
  }

  // Buyable Items endpoints (items available for purchase in-game)
  async getBuyableItems(params: BuyableItemsParams = {}): Promise<BuyableItem[]> {
    const response = await axios.get('/PersonalItems/buyable', { params });
    return response.data;
  }

  async getUniqueBuyableItems(params: BuyableItemsParams = {}): Promise<UniqueBuyableItem[]> {
    const response = await axios.get('/PersonalItems/buyable/unique', { params });
    return response.data;
  }

  async getAllShops(params: { shopType?: string; location?: string } = {}): Promise<ShopLocation[]> {
    const response = await axios.get('/PersonalItems/shops', { params });
    return response.data;
  }

  async getShopInventory(shopId: number): Promise<ShopInventoryItem[]> {
    const response = await axios.get(`/PersonalItems/shops/${shopId}/inventory`);
    return response.data;
  }

  async getAvailableShopTypes(): Promise<string[]> {
    const response = await axios.get('/PersonalItems/shops/types');
    return response.data;
  }

  async getAvailableShopLocations(): Promise<string[]> {
    const response = await axios.get('/PersonalItems/shops/locations');
    return response.data;
  }

  async getAvailableShopNames(): Promise<string[]> {
    const response = await axios.get('/PersonalItems/shops/names');
    return response.data;
  }

  async getBuyableFilterOptions(): Promise<BuyableFilterOptions> {
    const response = await axios.get('/PersonalItems/buyable/filter-options');
    return response.data;
  }
}

// Additional interfaces for buyable items
export interface BuyableItemsParams {
  shopType?: string;
  location?: string;
  shopName?: string;
  itemType?: string;
  subType?: string;
  category?: string;
  search?: string;
  skip?: number;
  take?: number;
}

export interface BuyableItem {
  id: number;
  itemId: string;
  displayName: string;
  buyPrice?: number;
  sellPrice?: number;
  maxStock?: number;
  shopId: number;
  shopName?: string;
  location?: string;
  shopType?: string;
}

export interface UniqueBuyableItem {
  itemId: string;
  displayName: string;
  itemType?: string;
  subType?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  shopCount: number;
  shops: ShopInfo[];
}

export interface BuyableFilterOptions {
  itemTypes: Array<{ type: string; count: number }>;
  subTypes: Array<{ subType: string; count: number }>;
  categories: Array<{ category: string; count: number }>;
  totalItems: number;
}

export interface ShopInfo {
  shopName?: string;
  location?: string;
  price?: number;
}

export interface ShopInventoryItem {
  id: number;
  shopId: number;
  itemName: string;
  displayName?: string;
  buyPrice?: number;
  sellPrice?: number;
  maxStock?: number;
  refreshRate?: number;
  isAvailable: boolean;
}

export const personalItemService = new PersonalItemService();
